/**
 * Registry central de módulos de CoWork.
 * Cada módulo exporta su config.js con rutas, menú, features y permisos.
 * El router y el sidebar leen este registry para armar la app dinámicamente.
 */
import mercadolibre from './mercadolibre/config'
import diagnostico from './diagnostico/config'
import comunicaciones from './comunicaciones/config'
import gestion from './gestion/config'
import plataforma from './plataforma/config'
import consultoria from './consultoria/config'
import asignaciones from './asignaciones/config'
import portal from './portal/config'
// Fase 3 — Módulo Productos
import productos from './productos/config'
// Fase 1A Rentabilidad — Simulador de costos por vender
import rentabilidad from './rentabilidad/config'
// Módulo Casos — Mayo 2026 (ver docs/casos.md)
import casos from './casos/config'
// Módulo Postventa — MVP Mayo 2026 (ver docs/postventa-mvp-plan.md)
import postventa from './postventa/config'
// Módulo IA — Plan IA Paso 2c (Mayo 2026). Sin UI propia: ítem contratable
// del plan; gatea la IA de la empresa vía modulosIncluidos.
import ia from './ia/config'
// Módulo Tutoriales — Capacitación interna (Bloque 1 — 26-may-2026)
// Grabación in-app de pantalla + mic + cámara opcional. Hosting Firebase.
// Ver ESTADO_ACTUAL.md sesión 26-may para el plan en 6 bloques.
import tutoriales from './tutoriales/config'
// Módulo Proyectos — "nuestro Asana" (jun-2026). Interno de Milbrands por ahora.
// Ver .claude/decisions/018-modulo-proyectos.md + docs/modulo_proyectos.md
import proyectos from './proyectos/config'
// Módulo Roadmap de desarrollo — tablero Now/Next/Later del equipo CoWork
// (17-jul-2026). Interno de Milbrands; agrupa bajo "Milbrands Operaciones".
import roadmap from './roadmap/config'
// Módulo Mercado — inteligencia de mercado sobre Market Pro (jun-2026).
// Ver docs/modulo_mercado.md + ADR 036/037. Explorador on-demand del consultor.
import mercado from './mercado/config'
// Módulo Supervisión — grupo de sidebar de oversight (7-jul-2026).
// Debajo de Gestión; arranca con Casos y Proyectos (solo ítems de menú).
import supervision from './supervision/config'
// Módulo Prospectiva — visión a futuro del seller (3-ago-2026). V1: Planificación
// de Compras (estacionalidad × stock × lead time). Sin rutas/sidebar: vive como
// primera solapa del grupo Dashboards de la ficha de empresa. Ver
// docs/PLAN_prospectiva-planificacion-compras.md.
import prospectiva from './prospectiva/config'
// Split de la sección Plataforma (7-jul-2026) — dos grupos debajo de
// Supervisión. Scaffolds vacíos hasta mudar ítems desde `plataforma`.
import plataformaOperacion from './plataforma-operacion/config'
import plataformaConfiguracion from './plataforma-configuracion/config'
import usuariosPermisos from './usuarios-permisos/config'
// Servicios de mercado del Pulso general (17-jul-2026) — SIN UI, ítems
// contratables del plan (mismo patrón que `ia`). Dos servicios DISTINTOS:
// datos de Market Pro para el cliente vs benchmark anónimo de la plataforma.
// El front gatea con tieneMercado()/tieneUniverso() (lib cuadroGeneral).
// Módulo Debugging — bug tracker interno (ADR 051) y ÚNICA cola de corrección
// del proyecto (ADR 064). Hasta el 7-ago-2026 era un módulo zombie: tenía lib/
// pero ni config ni registro acá, y su página colgaba de `plataforma/`.
import debugging from './debugging/config'
// Módulo Guardia — la pantalla única del Loop V2 (T0 PR-6): firma de Puerta
// Cero + estado del organismo + repaso de archivo. Reusa el permiso `debugging`.
import guardia from './guardia/config'
import mercadoDatos from './mercado-datos/config'
import universoMilbrands from './universo-milbrands/config'
import tiendaNube from './tienda-nube/config'
// Mi negocio (2-oct-2026) — los tableros que comparan canales (hoy, Precios).
// Sin rutas ni sidebar: vive como solapa de la ficha de empresa.
import miNegocio from './mi-negocio/config'
// Club Milbrands (20-ago-2026) — 4 módulos independientes y vendibles por
// separado; `club` es SOLO el grupo del sidebar que los agrupa (patrón
// `supervision`). Ola 1: Formación. Olas siguientes: Comunidad, Eventos,
// Empleo. Ver el ADR rector del Club + docs/NOTA_membresia-club.md.
import club from './club/config'
import formacion from './formacion/config'
import eventos from './eventos/config'
import eventosOrganizar from './eventos-organizar/config'
// Reportes (8-oct-2026) — reportes congelados de los dashboards. Ítem
// contratable del plan (pregunta 39 del triage del 10-oct); su carpeta es
// `reportes/` pero el id es `reportes-generados` (ver la cabecera del config).
import reportesGenerados from './reportes/config'

const modules = {
  mercadolibre,
  'tienda-nube': tiendaNube,
  'mi-negocio': miNegocio,
  mercado,
  'mercado-datos': mercadoDatos,
  'reportes-generados': reportesGenerados,
  'universo-milbrands': universoMilbrands,
  diagnostico,
  comunicaciones,
  gestion,
  supervision,
  prospectiva,
  'plataforma-operacion': plataformaOperacion,
  'usuarios-permisos': usuariosPermisos,
  'plataforma-configuracion': plataformaConfiguracion,
  club,
  formacion,
  eventos,
  'eventos-organizar': eventosOrganizar,
  asignaciones,
  plataforma,
  consultoria,
  portal,
  productos,
  rentabilidad,
  casos,
  postventa,
  ia,
  tutoriales,
  proyectos,
  roadmap,
  debugging,
  guardia,
}

/**
 * Devuelve los módulos activos para una empresa dada.
 * Si modulosActivados está vacío o undefined, devuelve todos (backward compatible).
 */
export function getActiveModules(modulosActivados) {
  if (!modulosActivados || modulosActivados.length === 0) {
    // Backward compatible: si la empresa no tiene módulos configurados, cargar todo
    return Object.values(modules)
  }
  return modulosActivados
    .map(id => modules[id])
    .filter(Boolean)
}

/**
 * Devuelve todos los módulos registrados.
 */
export function getAllModules() {
  return Object.values(modules)
}

/**
 * Devuelve un módulo por ID.
 */
export function getModule(id) {
  return modules[id] || null
}

export default modules
